import {
  CheckboxTreeView,
  TreeItemData,
} from '@aibees/elephant/components/checkbox-tree-view';
import { Classification } from '@aibees/elephant/feature/search-filters';
import { useLoadFilterDataApi } from '@aibees/elephant/feature/search-filters/hooks/use-load-filter-api';
import { useOptionsUrl } from '@aibees/elephant/feature/search-filters/utils';
import { MetadataItem, useSearchQueryMetadata } from '@aibees/elephant/hooks';
import { Icon, Scrollbar, Tab } from '@aibees/shared/components';
import { SearchProvider } from '@aibees/shared/context';
import { useStableReference } from '@aibees/shared/hooks';
import { fHumanReadableNumber } from '@aibees/shared/utils';
import { TabContext, TabList } from '@mui/lab';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFilteredTreeItems } from '../../hooks/use-filtered-tree-items';
import { useGetTreeItems } from '../../hooks/use-get-tree-items';
import { useIsSearchQueryReachedMinimumLengthThreshold } from '../../hooks/use-is-search-query-reached-minimum-length-threshold';
import { useItemsWithComma } from '../../hooks/use-items-with-comma';
import * as SX from './sx';
import type { AutocompleteTreeFilterDropdownProps as Props } from './types';

function areAutocompleteTreeFilterDropdownPropsEqual(
  prev: Props,
  next: Props
): boolean {
  if (prev.field !== next.field) return false;
  if (prev.focused !== next.focused) return false;
  if (prev.open !== next.open) return false;
  if (prev.debouncedSearchQuery !== next.debouncedSearchQuery) return false;
  if (prev.searchInputValue !== next.searchInputValue) return false;
  if (prev.hasComma !== next.hasComma) return false;
  if (prev.onChangeSelection !== next.onChangeSelection) return false;
  if (
    prev.handleCommaSeparatedChangeSelection !==
    next.handleCommaSeparatedChangeSelection
  )
    return false;
  if (prev.selectedValues.length !== next.selectedValues.length) return false;
  if (!prev.selectedValues.every((v, i) => v === next.selectedValues[i]))
    return false;
  if (prev.config !== next.config) return false;
  if (prev.metadata !== next.metadata) return false;
  if (prev.utils !== next.utils) return false;
  if (prev.treeOptions !== next.treeOptions) return false;
  if (prev.options !== next.options) return false;
  if (prev.searchKey !== next.searchKey) return false;
  if (prev.tabs !== next.tabs) return false;
  if (prev.icon !== next.icon) return false;
  return true;
}

export const AutocompleteTreeFilterDropdown = memo(
  ({
    focused,
    metadata,
    config,
    field: _field,
    utils,
    icon: filterIcon,
    treeOptions,
    options,
    searchKey,
    tabs,
    open: filterOpen,
    debouncedSearchQuery,
    searchInputValue,
    selectedValues,
    onChangeSelection,
    handleCommaSeparatedChangeSelection,
    hasComma,
    ..._rest
  }: Props) => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<number>(0);
    const customOptionsLimit = metadata?.autocomplete?.customOptionsLimit;
    const isSearchQueryReachedMinimumLengthThreshold =
      useIsSearchQueryReachedMinimumLengthThreshold(
        debouncedSearchQuery,
        metadata?.autocomplete?.searchTriggerMinLength
      );

    const url = useOptionsUrl(options, tabValue, tabs);
    const apiParams = useMemo(
      () =>
        debouncedSearchQuery && !metadata?.autocomplete?.onlySearchInResults
          ? {
              [searchKey || (config?.labelKey as string)]: debouncedSearchQuery,
            }
          : {},
      [
        debouncedSearchQuery,
        metadata?.autocomplete?.onlySearchInResults,
        searchKey,
        config?.labelKey,
      ]
    );
    const optionsApi = useLoadFilterDataApi<Classification>(
      url!,
      apiParams,
      utils,
      tabValue,
      options,
      metadata?.autocomplete?.tabsCountBadge,
      tabs
    );

    const loading = !!url && !!optionsApi && optionsApi.isLoading;

    const metadataApi = useSearchQueryMetadata(
      metadata?.metaDataApi?.key,
      !!(filterOpen && metadata?.metaDataApi?.enabled)
    );

    const metadataApiData = useStableReference(metadataApi.data, (data) =>
      JSON.stringify(data?.map((item: MetadataItem) => item.value) ?? [])
    );

    // Static options: compute items for all tabs once at mount, then use by tab.
    const [itemsByTab, setItemsByTab] = useState<Map<
      number,
      TreeViewBaseItem<TreeItemData>[]
    > | null>(null);
    useEffect(() => {
      if (
        Array.isArray(treeOptions) &&
        treeOptions.length > 0 &&
        itemsByTab === null
      ) {
        const map = new Map<number, TreeViewBaseItem<TreeItemData>[]>();
        treeOptions.forEach((opt, i) => {
          if (Array.isArray(opt) && opt.length > 0) {
            map.set(i, opt as TreeViewBaseItem<TreeItemData>[]);
          }
        });
        if (map.size > 0) {
          setItemsByTab(map);
        }
      }
    }, [treeOptions, itemsByTab]);

    const treeItems: TreeViewBaseItem<TreeItemData>[] = useMemo(() => {
      return itemsByTab?.get(tabValue) ?? itemsByTab?.get(0) ?? [];
    }, [itemsByTab, tabValue]);

    const { items } = useGetTreeItems(
      config,
      metadata,
      utils,
      filterIcon,
      treeItems,
      undefined,
      metadataApiData,
      tabValue,
      selectedValues,
      metadata?.autocomplete?.multiple
    );

    const filteredTreeItems = useFilteredTreeItems({
      items,
      debouncedSearchQuery,
      isGettingDataFromApi: false,
      metadata,
      customOptionsLimit,
    });

    const itemsWithComma = useItemsWithComma({
      searchInputValue,
    });

    const handleChangeTab = useCallback(
      (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
      },
      []
    );

    const hasItemsToShow = hasComma
      ? itemsWithComma.length > 0
      : filteredTreeItems.length > 0;

    return (
      <Card variant="outlined" color={focused ? 'primary' : 'default'}>
        <CardContent padding="none">
          <Box
            display="flex"
            flexDirection="column"
            paddingX={0.5}
            paddingY={tabs?.length && tabs?.length > 1 ? 1 : 0}
          >
            {!!tabs?.length && tabs?.length > 1 && (
              <TabContext value={tabValue}>
                <TabList
                  onChange={handleChangeTab}
                  variant="fullWidth"
                  size="medium"
                >
                  {tabs?.map((tab, i) => (
                    <Tab
                      key={tab}
                      label={tab}
                      value={i}
                      LabelVariant="soft"
                      LabelContent={
                        !url ? undefined : optionsApi.isLoading ? (
                          <Icon name="loading-right-outline" />
                        ) : (
                          fHumanReadableNumber(
                            optionsApi.data?.metadata?.tabsCount?.[i]
                          )
                        )
                      }
                    />
                  ))}
                </TabList>
              </TabContext>
            )}
            <Box height={!hasItemsToShow ? theme.spacing(18) : 'none'}>
              <Scrollbar sx={SX.scrollbar}>
                {loading ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    height={theme.spacing(18)}
                    width="100%"
                  >
                    <Icon name="hourglass-empty" size={18} color="grey.400" />
                    <Typography variant="body5" color="grey.500">
                      Loading...
                    </Typography>
                  </Box>
                ) : (
                  <SearchProvider>
                    {hasItemsToShow ? (
                      <CheckboxTreeView
                        items={hasComma ? itemsWithComma : items}
                        filteredItems={
                          hasComma ? itemsWithComma : filteredTreeItems
                        }
                        onChange={
                          hasComma
                            ? handleCommaSeparatedChangeSelection
                            : onChangeSelection
                        }
                        search={debouncedSearchQuery ?? ''}
                        multiSelect={metadata?.autocomplete?.multiple}
                        checkboxPosition={
                          metadata?.autocomplete?.checkboxPosition
                        }
                        metadataEnabled={metadata?.metaDataApi?.enabled}
                        metadataApiLoading={metadataApi.isFetching}
                        iconSX={metadata?.autocomplete?.iconSx}
                        avartarFallbackIcon={
                          metadata?.autocomplete?.avartarFallbackIcon
                        }
                        hideLogoFallback={
                          metadata?.autocomplete?.hideLogoFallback
                        }
                        isSearchQueryReachedMinimumLengthThreshold={
                          isSearchQueryReachedMinimumLengthThreshold
                        }
                      />
                    ) : (
                      <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        height={theme.spacing(18)}
                        width="100%"
                      >
                        <Typography variant="body4" color="grey.500">
                          {debouncedSearchQuery
                            ? 'No results found'
                            : metadata?.emptyMessage || 'Nothing Here Yet'}
                        </Typography>
                      </Box>
                    )}
                  </SearchProvider>
                )}
              </Scrollbar>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  },
  areAutocompleteTreeFilterDropdownPropsEqual
);

AutocompleteTreeFilterDropdown.displayName = 'AutocompleteTreeFilterDropdown';
